import React, { useLayoutEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { PatientProfile } from '../../api/services/orders/types.ts'
import Breadcrumbs from '../../components/common/Breadcrumbs/Breadcrumbs.tsx'
import Card from '../../components/common/Card/Card.tsx'
import DataEntryForm, {
  FormField,
  SelectOption
} from '../../components/common/Table/DataEntryForm.tsx'
import usePatientProfile from '../../hooks/usePatientProfile.ts'
import { usePatientProfileMutations } from '../../hooks/usePatientProfileMutations.ts'
import { useUserAssignmentCode } from '../../hooks/useUserAssignmentCode.ts'
import { capitalizeFirstLetter } from '../../utils/bodyParts/helpers.ts'

const CreatePatientProfile = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const patientProfile: PatientProfile = location.state?.patientProfile

  const { profileTypes, getPatientTypes } = usePatientProfile()
  const { userCodes: userAssignmentCodes } = useUserAssignmentCode()

  const getSelectValue = (value: unknown) => {
    if (value && typeof value === 'object' && 'id' in value) {
      return (value as { id: string | number }).id
    }

    return value
  }

  const getPatientProfileEditValues = (
    patientProfile: PatientProfile
  ): Partial<PatientProfile> | null => {
    if (!patientProfile) {
      return null
    }

    const selectedUserAssignmentCode = userAssignmentCodes?.find(
      (item) => item.code === patientProfile.user_assignment_code
    )

    const selectedPatientType = profileTypes?.find(
      (item) => item.name === patientProfile.patient_type
    )

    return {
      ...patientProfile,
      patient_type: selectedPatientType?.id,
      sex: getSelectValue(patientProfile.sex),
      user_assignment_code: selectedUserAssignmentCode?.id
    } as Partial<PatientProfile>
  }

  const editValues = userAssignmentCodes
    ? getPatientProfileEditValues(patientProfile)
    : null

  const onClose = () => {
    if (patientProfile) {
      navigate(`/patient-profile/${patientProfile.id}`)
    } else {
      navigate('/patient-profile')
    }
  }

  const { createPatientProfileMutation, editPatientProfileMutation } =
    usePatientProfileMutations({
      onSuccess: onClose
    })

  useLayoutEffect(() => {
    const fetchTypes = async () => {
      await getPatientTypes()
    }
    fetchTypes()
  }, [])

  const normalizeValuesToLowerCase = <T extends Record<string, unknown>>(
    values: T
  ): T => {
    return Object.fromEntries(
      Object.entries(values).map(([key, value]) => {
        const valueFormated =
          typeof value === 'string'
            ? key === 'first_name' || key === 'last_name'
              ? capitalizeFirstLetter(value)
              : value.toLowerCase()
            : value

        return [key, valueFormated]
      })
    ) as T
  }

  const onSubmit = async (values: any) => {
    const normalizedValues = normalizeValuesToLowerCase(values)
    const response = patientProfile
      ? await editPatientProfileMutation.mutateAsync({
          profilePatientId: patientProfile.id,
          profilePatientData: normalizedValues
        })
      : await createPatientProfileMutation.mutateAsync(normalizedValues)

    if (response.status === 201 && response.data && response.data.id) {
      const createdProfileId = response.data.id
      navigate(`/patient-profile/${createdProfileId}/`)
    }
  }

  const typesOptions: SelectOption[] | undefined = profileTypes
    ? profileTypes?.map((type) => ({
        label: type.name,
        value: type.id
      }))
    : undefined

  const userCodesOptions: SelectOption[] | undefined = userAssignmentCodes
    ? userAssignmentCodes?.map((item) => ({
        label: item.code,
        value: item.id
      }))
    : undefined

  const formFields: FormField[] = [
    {
      name: 'patient_type',
      label: 'Typ pacjenta*',
      type: 'select',
      placeholder: 'Wybierz pasujący typ pacjenta:',
      required: true,
      options: typesOptions,
      break: true
    },
    {
      name: 'sex',
      label: 'Płeć*',
      type: 'select',
      placeholder: 'Wybierz z listy:',
      required: true,
      options: [
        { label: 'Mężczyzna', value: 1 },
        { label: 'Kobieta', value: 2 }
      ]
    },
    {
      name: 'first_name',
      label: 'Imię*',
      placeholder: 'wprowadź imię pacjenta',
      required: true
    },
    {
      name: 'last_name',
      label: 'Nazwisko*',
      placeholder: 'wprowadź nazwisko pacjenta',
      required: true,
      break: true
    },
    {
      name: 'preassigned_email',
      label: 'Adres e-mail ( rejestracja konta w aplikacji )*',
      placeholder: 'wprowadź e-mail',
      required: true,
      break: true
    },
    {
      name: 'user_assignment_code',
      label: 'Kod przypisania*',
      type: 'select',
      placeholder: 'Wybierz kod przypisania:',
      required: true,
      options: userCodesOptions,
      break: true
    }
  ]

  const createBreadCrumbs = () => {
    const breadcrumbs = []
    breadcrumbs.push({ label: 'Pacjenci', path: '/patient-profile' })
    if (patientProfile) {
      breadcrumbs.push({
        label: `${patientProfile.first_name} ${patientProfile.last_name}`,
        path: `/patient-profile/${patientProfile.id}`
      })
      breadcrumbs.push({ label: 'Edycja danych' })
    } else {
      breadcrumbs.push({ label: 'Dodawanie pacjenta' })
    }

    return breadcrumbs
  }

  return (
    <React.Fragment>
      <Breadcrumbs
        pageName={
          patientProfile ? 'Edycja profilu pacjenta' : 'Utwórz nowego pacjenta'
        }
        breadcrumbs={createBreadCrumbs()}
      />

      <React.Fragment>
        <Card className="w-full mb-4">
          <Card.Body className={'px-4 !py-4 md:px-7 md:!py-4'}>
            {userAssignmentCodes && profileTypes && (
              <DataEntryForm
                fields={formFields}
                onSubmit={onSubmit}
                submitLabel={
                  patientProfile ? 'Zapisz dane' : 'Dodaj profil pacjenta'
                }
                onClose={onClose}
                mode={patientProfile ? 'edit' : 'add'}
                editValues={editValues}
              />
            )}
          </Card.Body>
        </Card>
      </React.Fragment>
    </React.Fragment>
  )
}

export default CreatePatientProfile
